import { useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api';
import { notify } from '../../lib/notify';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { RoleGuard } from '../../components/auth/RoleGuard';
import { PermissionsSelector } from '../../components/settings/PermissionsSelector';

interface Permission {
  id: string;
  name: string;
  category: string;
}

interface Role {
  id: string;
  name: string;
  permissions: { id: string; name: string }[];
  userCount?: number;
  createdAt: string;
}

const SYSTEM_ROLES = ['platformAdmin', 'tenantAdmin', 'hubManager', 'spokeManager', 'gigWorker', 'admin', 'user'];

export default function RolesList() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  async function loadRoles() {
    try {
      const res = await apiFetch('/api/v1/roles');
      if (!res.ok) {
        if (res.status === 401) throw new Error('Please log in again');
        const data = await res.json().catch(() => ({}));
        const message = res.status >= 500 ? 'Something went wrong. Please try again later.' : (data.error || 'Failed to load roles');
        throw new Error(message);
      }
      const data = await res.json();
      setRoles(data.items || data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load roles');
    } finally {
      setIsLoading(false);
    }
  }

  async function loadPermissions() {
    try {
      const res = await apiFetch('/api/v1/roles/permissions');
      if (!res.ok) {
        if (res.status === 401) return;
        const data = await res.json().catch(() => ({}));
        const message = res.status >= 500 ? 'Something went wrong. Please try again later.' : (data.error || 'Failed to load permissions');
        throw new Error(message);
      }
      const data = await res.json();
      setPermissions(Array.isArray(data) ? data : data.items || []);
    } catch {
      setPermissions([]);
    }
  }

  useEffect(() => {
    loadRoles();
    loadPermissions();
  }, []);

  async function createRole() {
    if (!newRoleName.trim() || selectedPermissions.length === 0) return;

    try {
      const res = await apiFetch('/api/v1/roles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newRoleName,
          permissionIds: selectedPermissions,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const message = res.status >= 500 ? 'Something went wrong. Please try again later.' : (data.error || 'Failed to create role');
        throw new Error(message);
      }

      setCreateOpen(false);
      setNewRoleName('');
      setSelectedPermissions([]);
      loadRoles();
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Failed to create role');
    }
  }

  async function deleteRole(id: string) {
    if (!window.confirm('Are you sure you want to delete this role?')) return;

    try {
      const res = await apiFetch(`/api/v1/roles/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const message = res.status >= 500 ? 'Something went wrong. Please try again later.' : (data.error || 'Failed to delete role');
        throw new Error(message);
      }
      loadRoles();
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Failed to delete role');
    }
  }

  function togglePermission(permissionId: string) {
    setSelectedPermissions(prev =>
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  }

  function isSystemRole(name: string): boolean {
    return SYSTEM_ROLES.includes(name);
  }

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="space-y-6">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen">
        <div className="space-y-6">
          <p className="text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold">Roles</h1>
          <RoleGuard permission="roles.write">
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button>Create Role</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Role</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4">
                  <div>
                    <Label htmlFor="roleName">Role Name</Label>
                    <Input
                      id="roleName"
                      value={newRoleName}
                      onChange={e => setNewRoleName(e.target.value)}
                      placeholder="Enter role name"
                    />
                  </div>
                  <div>
                    <Label>Permissions</Label>
                    <div className="mt-2">
                      <PermissionsSelector
                        permissions={permissions}
                        selectedIds={selectedPermissions}
                        onToggle={togglePermission}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setCreateOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={createRole}
                      disabled={!newRoleName.trim() || selectedPermissions.length === 0}
                    >
                      Create Role
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </RoleGuard>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>All Roles</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Role Name</TableHead>
                  <TableHead className="hidden sm:table-cell">Permissions</TableHead>
                  <TableHead className="hidden md:table-cell">Users</TableHead>
                  <TableHead className="hidden sm:table-cell">Type</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map(role => (
                  <TableRow key={role.id}>
                    <TableCell className="font-medium">{role.name}</TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant="secondary">
                        {role.permissions.length} permissions
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{role.userCount ?? 0}</TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {isSystemRole(role.name) ? (
                        <Badge variant="outline">System</Badge>
                      ) : (
                        <Badge>Custom</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/settings/roles/${role.id}`}>View</Link>
                        </Button>
                        <RoleGuard permission="roles.write">
                          {!isSystemRole(role.name) && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => deleteRole(role.id)}
                              disabled={role.userCount !== undefined && role.userCount > 0}
                            >
                              Delete
                            </Button>
                          )}
                        </RoleGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
